/* v5.2 Score Engine constants — single source of truth for the buyer
 * score labels surfaced in: BuyerScore · SellerBuyers · AdminBuyers ·
 * Lender/Bank dashboards. Backend ships `weights` on every /score
 * response so frontend reads weights live; only the LABELS need to be
 * synced here when buckets change.
 *
 * v5.2 (Feb 2026): KYB + Identity moved to Stage-1 Gate (display-only
 * in breakdown). Two new buckets: public_records (10%) and
 * concentration_velocity (5%).
 */

export const SCORE_SIGNAL_LABELS = {
  net_terms_payment:       "Net-terms payment behaviour",
  credit_utilization:      "Credit utilization",
  default_events:          "Default & dispute events",
  cashflow:                "Cashflow (Plaid)",
  credit_history:          "Credit-line history",
  ghosting:                "Ghosting & ops risk",
  tenure:                  "Tenure & ecosystem",
  wallet:                  "Wallet activity",
  public_records:          "Public records (UCC · liens · judgments)",
  concentration_velocity:  "Concentration & velocity",
  // Display-only (computed but not weighted — surfaced in trust panel):
  kyb:                     "KYB verification (Stage-1 gate)",
  identity:                "Identity & security (Stage-1 gate)",
};

// Fallback weights for legacy callers that don't pass weights via the
// API. Always prefer the `weights` field from `/api/score`.
export const SCORE_SIGNAL_FALLBACK_WEIGHTS_PCT = {
  net_terms_payment:       22,
  default_events:          15,
  cashflow:                12,
  credit_utilization:      10,
  wallet:                  10,
  public_records:          10,
  credit_history:          8,
  ghosting:                5,
  concentration_velocity:  5,
  tenure:                  3,
};

// Buckets included in the WEIGHTED composite (Stage-2 score). Use this
// to filter `signals` when rendering the "weighted contribution" bars.
export const SCORE_WEIGHTED_KEYS = Object.keys(
  SCORE_SIGNAL_FALLBACK_WEIGHTS_PCT,
);

// Display-only buckets — render in the "Stage-1 gate" panel separately
// from the weighted score bars.
export const SCORE_STAGE1_GATE_KEYS = ["kyb", "identity"];

// ─── v5.2 · Neutral tier copy (Feb 2026) ──────────────────────────────
//
// Tier labels remain the same observable bands as before EXCEPT
// "High risk" — renamed to "Limited history" to remove prescriptive
// credit-judgement language. verqo provides DATA; sellers/lenders make
// the credit decision.

export const SCORE_TIER_PILL_STYLES = {
  Excellent:         "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Very Good":       "bg-emerald-50 text-emerald-700 border-emerald-200",
  Good:              "bg-blue-50 text-blue-700 border-blue-200",
  Fair:              "bg-amber-50 text-amber-700 border-amber-200",
  Building:          "bg-orange-50 text-orange-700 border-orange-200",
  "Limited history": "bg-slate-100 text-slate-700 border-slate-300",
  // legacy fallback (pre-v5.2 cached responses)
  "High risk":       "bg-slate-100 text-slate-700 border-slate-300",
};

// Neutral one-line explainer per tier — surfaced as popovers/tooltips
// alongside the rating pill so users understand what the label means
// WITHOUT receiving a credit recommendation from verqo.
export const SCORE_TIER_EXPLAINERS = {
  Excellent:         "Long track record of on-time payment, healthy cashflow, and clean public records.",
  "Very Good":       "Consistent on-time payment with strong supporting data across most signals.",
  Good:              "Solid payment history with adequate cashflow and verification data.",
  Fair:              "Mixed signals — some late payments or thinner supporting data.",
  Building:          "Limited but mostly positive history — early-stage trading relationship.",
  "Limited history": "Insufficient trade data to characterize behaviour — not a credit judgement.",
  "High risk":       "Insufficient trade data to characterize behaviour — not a credit judgement.",
};

// Render this disclaimer at the top of every score panel surfaced to
// sellers, lenders and admins. Mirrors `disclaimer` field returned by
// /api/score (kept here as a fallback for surfaces that don't fetch it).
export const SCORE_DISCLAIMER =
  "The Verqo Trade Score is a neutral, behaviour-based summary of trading data and " +
  "third-party verification signals. It is informational only and is NOT a " +
  "credit decision, approval, or recommendation from verqo. Reference terms " +
  "and limits are peer-anchored data points — lenders and sellers set their " +
  "own underwriting policy and remain solely responsible for credit decisions.";

// Helper · normalize legacy "High risk" rating strings to "Limited history"
// so older cached score docs still render the new label everywhere.
export function normalizeRating(rating) {
  if (!rating) return rating;
  return rating === "High risk" ? "Limited history" : rating;
}
